import { useState, useContext, useEffect } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import DeleteIcon from "@mui/icons-material/Delete"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd"
import ListAltIcon from "@mui/icons-material/ListAlt"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import CloseIcon from "@mui/icons-material/Close"
import DownloadIcon from "@mui/icons-material/Download"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const accordionStyles = {
  mb: 2,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "8px !important",
  boxShadow: "none",
  "&:before": { display: "none" },
}

const AsignarCartera = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [formData, setFormData] = useState({ usrasignado: "", comentario: "" })
  const [cuotas, setCuotas] = useState([])
  const [totalAsignar, setTotalAsignar] = useState(0)

  // =======================================================================
  // ESTADOS PARA OPTIMIZACIÓN Y VISOR DE PDF
  // =======================================================================
  const [pdfCache, setPdfCache] = useState({})
  const [pdfLoading, setPdfLoading] = useState({})
  const [pdfModalOpen, setPdfModalOpen] = useState(false)
  const [currentPdf, setCurrentPdf] = useState({ base64: "", facnumfac: "" })

  useEffect(() => {
    if (location.state?.cuotasSeleccionadas) {
      setCuotas(location.state.cuotasSeleccionadas)
    } else {
      navigate("/home/dashboard/AsignaciondeCartera")
    }
  }, [location, navigate])

  useEffect(() => {
    const total = cuotas.reduce((acc, curr) => acc + (parseFloat(curr.facsaldo) || 0), 0)
    setTotalAsignar(total)
  }, [cuotas])

  const { data: rawUsuariosData, isLoading: isLoadingUsuarios } = useQuery({
    queryKey: ["getUsuariosAsignables"],
    queryFn: async () => {
      try {
        const response = await api.post("/CreacionUsuarios/getAllUsuarios", {
          page: 1,
          perPage: 1000,
          filters: { usrstatus: "A", usrflagoficre: -1 },
        })
        return response?.data?.data?.data || response?.data?.data || response?.data || []
      } catch (error) {
        return []
      }
    },
    refetchOnWindowFocus: false,
  })

  const listaUsuarios = (Array.isArray(rawUsuariosData) ? rawUsuariosData : []).map((u) => ({
    id: u.usrcodigo,
    label: `${u.usrcodigo} - ${u.usrnombre || ""}`,
  }))

  const { mutateAsync: SaveAsignacion, isPending: isSaving } = useMutation({
    queryKey: ["isCreatingAsignacionCartera"],
    fn: async (dataPayload) => (await api.post("/AsignaciondeCartera/createAsignaciondeCartera", dataPayload)).data,
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  const handleRemoveCuota = (idx) => {
    const nCuotas = [...cuotas]
    nCuotas.splice(idx, 1)
    setCuotas(nCuotas)
  }

  // =======================================================================
  // LÓGICA DE EXTRACCIÓN Y VISOR DE PDF
  // =======================================================================
  const handleViewPDF = async (facnumfac) => {
    if (pdfCache[facnumfac]) {
      setCurrentPdf({ base64: pdfCache[facnumfac], facnumfac })
      setPdfModalOpen(true)
      return
    }

    try {
      setPdfLoading((prev) => ({ ...prev, [facnumfac]: true }))

      const response = await api.post("/AsignaciondeCartera/getFacturaPDF", { facnumfac })

      let base64PDF = ""
      const responseBody = response?.data
      const responseData = responseBody?.data

      if (Array.isArray(responseData)) {
        if (responseData[0] && responseData[0].data) {
          base64PDF = responseData[0].data
        }
      } else if (responseData && typeof responseData === "object") {
        base64PDF = responseData.data || responseData.sripdf
      } else if (typeof responseData === "string") {
        base64PDF = responseData
      }

      if (base64PDF && typeof base64PDF === "string" && base64PDF.trim() !== "") {
        setPdfCache((prev) => ({ ...prev, [facnumfac]: base64PDF }))
        setCurrentPdf({ base64: base64PDF, facnumfac })
        setPdfModalOpen(true)
      } else {
        showWarning(`No se encontró un documento PDF asociado a la factura ${facnumfac}.`)
      }
    } catch (error) {
      console.error("Error al obtener el PDF:", error)
      showWarning("Ocurrió un error al intentar descargar el documento PDF.")
    } finally {
      setPdfLoading((prev) => ({ ...prev, [facnumfac]: false }))
    }
  }

  // Helper para generar el enlace de descarga a partir del Base64
  const descargarBase64PDF = (base64, facnumfac) => {
    try {
      const linkSource =
        base64.startsWith("JVBER") || !base64.includes("data:application/pdf;base64,")
          ? `data:application/pdf;base64,${base64}`
          : base64

      const downloadLink = document.createElement("a")
      downloadLink.href = linkSource
      downloadLink.download = `Factura_SRI_${facnumfac}.pdf`
      downloadLink.click()
    } catch (error) {
      showWarning("El archivo PDF recibido está corrupto o tiene un formato inválido.")
    }
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!formData.usrasignado) return showWarning("Debe seleccionar un recaudador para la asignación.")
    if (cuotas.length === 0) return showWarning("La lista de cartera está vacía. Seleccione cuotas para asignar.")

    const payload = {
      usrasignado: formData.usrasignado,
      comentario: formData.comentario,
      cuotas: cuotas.map((c) => ({ facnumfac: c.facnumfac, facsecuen: c.facsecuen })),
    }
    await SaveAsignacion(payload)
  }

  const action = selectedMenuInfo?.data?.barraAcciones?.find(
    (a) => a.acccaption === "GRABAR" || a.acccaption === "ASIGNAR",
  )

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2 }}>
          {action && (
            <Tooltip title={action.acccaption}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSaving || isLoadingUsuarios}
                sx={{ border: "1px solid #ddd", bgcolor: "white", borderRadius: 1 }}
              >
                {getIconComponent(action.accnameicono, action.acctipoico)}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <div style={{ display: "flex", justifyContent: "center", mb: "30px", fontSize: "25px" }}>
          <b>Ejecutar Asignación de Cartera</b>
        </div>

        <CustomBackdrop isLoading={isSaving || isLoadingUsuarios} />

        <Box sx={StyledRoot} component="form" onSubmit={handleSubmit}>
          <Accordion defaultExpanded sx={accordionStyles}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AssignmentIndIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  PARÁMETROS DE ASIGNACIÓN
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={listaUsuarios}
                    getOptionLabel={(o) => o.label || ""}
                    value={listaUsuarios.find((u) => u.id === formData.usrasignado) || null}
                    onChange={(e, v) => setFormData((prev) => ({ ...prev, usrasignado: v ? v.id : "" }))}
                    renderInput={(p) => (
                      <TextField
                        {...p}
                        label="Seleccione el Recaudador Asignado *"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monto Total a Asignar ($)"
                    value={totalAsignar.toFixed(2)}
                    InputProps={{ readOnly: true }}
                    sx={{ bgcolor: "#f9f9f9", input: { fontWeight: "bold", color: "#b71c1c", textAlign: "right" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    fullWidth
                    label="Instrucciones o Comentario (Opcional)"
                    value={formData.comentario}
                    onChange={(e) => setFormData((prev) => ({ ...prev, comentario: e.target.value.toUpperCase() }))}
                    placeholder="Ej: GESTIONAR URGENTE ESTA SEMANA"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded sx={accordionStyles}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ListAltIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  DETALLE DE LA CARTERA ({cuotas.length} Cuotas)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell align="center">
                        <b>#</b>
                      </TableCell>
                      <TableCell align="center">
                        <b>Factura</b>
                      </TableCell>
                      <TableCell align="center">
                        <b>Cuota</b>
                      </TableCell>
                      <TableCell>
                        <b>Cliente</b>
                      </TableCell>
                      <TableCell align="center">
                        <b>Vencimiento</b>
                      </TableCell>
                      <TableCell align="right">
                        <b>Saldo</b>
                      </TableCell>
                      <TableCell align="center">
                        <b>Doc. SRI</b>
                      </TableCell>
                      <TableCell align="center">
                        <b>Quitar</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cuotas?.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell align="center">{idx + 1}</TableCell>
                        <TableCell align="center">{row.facnumfac}</TableCell>
                        <TableCell align="center">{row.facsecuen}</TableCell>
                        <TableCell>{row.clinombre}</TableCell>
                        <TableCell align="center">{row.facfecven}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          ${Number(row.facsaldo).toFixed(2)}
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title="Ver Documento PDF">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewPDF(row.facnumfac)}
                                disabled={pdfLoading[row.facnumfac]}
                              >
                                {pdfLoading[row.facnumfac] ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <PictureAsPdfIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>

                        <TableCell align="center">
                          <IconButton size="small" color="error" onClick={() => handleRemoveCuota(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* ============================================================ */}
        {/* MODAL DEL VISOR DE PDF NATIVO                                */}
        {/* ============================================================ */}
        <Dialog
          open={pdfModalOpen}
          onClose={() => setPdfModalOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { height: "90vh", display: "flex", flexDirection: "column" } }}
        >
          <DialogTitle
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#f5f5f5" }}
          >
            <Typography variant="h6" fontWeight="bold" color="primary">
              Documento SRI - {currentPdf.facnumfac}
            </Typography>
            <IconButton onClick={() => setPdfModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider />

          <DialogContent sx={{ p: 0, flexGrow: 1, overflow: "hidden", backgroundColor: "#525659" }}>
            {currentPdf.base64 && (
              <iframe
                title="Visor PDF"
                src={
                  currentPdf.base64.startsWith("JVBER") || !currentPdf.base64.includes("data:application/pdf;base64,")
                    ? `data:application/pdf;base64,${currentPdf.base64}`
                    : currentPdf.base64
                }
                width="100%"
                height="100%"
                style={{ border: "none" }}
              />
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2, bgcolor: "#f5f5f5" }}>
            <Button onClick={() => setPdfModalOpen(false)} color="inherit" sx={{ fontWeight: "bold" }}>
              Cerrar
            </Button>
            <Button
              onClick={() => descargarBase64PDF(currentPdf.base64, currentPdf.facnumfac)}
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              sx={{ fontWeight: "bold" }}
            >
              Descargar Archivo
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  )
}

export default AsignarCartera
